import { useState, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination as MuiPagination,
  IconButton,
  Grid,
  TableCell,
  Checkbox
} from '@mui/material';
import { Add, Edit, Delete, BatteryChargingFull, Download, Archive, PlaylistAdd, DeleteOutline } from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import { useTranslation } from 'react-i18next';
import QRCode from 'qrcode';
import JSZip from 'jszip';
import {
  useGetBatteriesQuery,
  useGetBatteryStatsQuery,
  useCreateBatteryMutation,
  useUpdateBatteryMutation,
  useDeleteBatteryMutation,
  useGetChargingStationsQuery,
  useGetScooterTypesQuery,
  batteriesApi
} from '../../../redux/api/batteriesApi';
import {
  ScooterBattery,
  CreateBatteryBody,
  ScooterBatteryType,
  ScooterBatteryStatus,
  ScooterBatteryLocation
} from '../../../resources/types/batteriesTypes';
import { TableCell as MuiTableCell } from '@mui/material';
import moment from 'moment';

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.08)'
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.08)'
      : 'rgba(255, 255, 255, 0.1)'
  }`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
  padding: '12px 16px'
}));

const PaginationWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 24
});

const StatCard = styled(Paper)(({ theme }) => ({
  padding: '16px 20px',
  borderRadius: '12px',
  display: 'flex',
  flexDirection: 'column',
  gap: 8
}));

const BatteriesTab = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [page, setPage] = useState(1);
  const [searchCode, setSearchCode] = useState('');
  const [statusFilter, setStatusFilter] = useState<ScooterBatteryStatus | ''>('');
  const [scooterTypeFilter, setScooterTypeFilter] = useState<string>('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingBattery, setEditingBattery] = useState<ScooterBattery | null>(null);
  const [formData, setFormData] = useState<Partial<CreateBatteryBody>>({
    code: '',
    comment: '',
    scooterTypes: [],
    type: ScooterBatteryType.REPLACEABLE,
    status: ScooterBatteryStatus.ACTIVE,
    chargeLevel: 100,
    location: ScooterBatteryLocation.WAREHOUSE,
    scooter: null,
    chargingStation: null,
    faultCount: 0
  });

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkFormData, setBulkFormData] = useState({
    prefix: 'ACT-',
    startNumber: 1004,
    quantity: 800,
    scooterType: ''
  });
  const [bulkProgress, setBulkProgress] = useState({ current: 0, total: 0, isRunning: false, errors: [] as string[] });
  const [selectedBatteries, setSelectedBatteries] = useState<Set<string>>(new Set());

  const queryParams = useMemo(() => ({
    page,
    limit: 20,
    code: searchCode || undefined,
    status: statusFilter || undefined
  }), [page, searchCode, statusFilter]);

  const { data: batteriesData, isLoading } = useGetBatteriesQuery(queryParams);

  const filteredBatteries = useMemo(() => {
    if (!batteriesData?.data) return [];
    
    let filtered = batteriesData.data;
    
    if (scooterTypeFilter) {
      filtered = filtered.filter(battery => 
        battery.scooterTypes?.some(t => (typeof t === 'string' ? t : t._id) === scooterTypeFilter)
      );
    }
    
    if (searchCode) {
      const codeNumbers = searchCode.replace(/\D/g, '');
      filtered = filtered.filter(battery => 
        battery.code.replace(/\D/g, '').includes(codeNumbers)
      );
    }
    
    return filtered;
  }, [batteriesData?.data, scooterTypeFilter, searchCode]);
  const { data: stats } = useGetBatteryStatsQuery();
  const { data: stationsData } = useGetChargingStationsQuery();
  const { data: scooterTypesData } = useGetScooterTypesQuery();
  const [createBattery] = useCreateBatteryMutation();
  const [updateBattery] = useUpdateBatteryMutation();
  const [deleteBattery] = useDeleteBatteryMutation();

  const handleOpenDialog = (battery?: ScooterBattery) => {
    if (battery) {
      setEditingBattery(battery);
      setFormData({
        code: battery.code,
        comment: battery.comment || '',
        scooterTypes: battery.scooterTypes?.map(t => (typeof t === 'string' ? t : t._id)) || [],
        type: battery.type,
        status: battery.status,
        chargeLevel: battery.chargeLevel,
        location: battery.location,
        scooter: battery.scooter?._id || null,
        chargingStation: battery.chargingStation?._id || null,
        lastScanDate: battery.lastScanDate ? moment(battery.lastScanDate).format('YYYY-MM-DD') : undefined,
        lastServiceDate: battery.lastServiceDate ? moment(battery.lastServiceDate).format('YYYY-MM-DD') : undefined,
        faultCount: battery.faultCount
      });
    } else {
      setEditingBattery(null);
      setFormData({
        code: '',
        comment: '',
        scooterTypes: [],
        type: ScooterBatteryType.REPLACEABLE,
        status: ScooterBatteryStatus.ACTIVE,
        chargeLevel: 100,
        location: ScooterBatteryLocation.WAREHOUSE,
        scooter: null,
        chargingStation: null,
        faultCount: 0
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingBattery(null);
  };

  const handleSave = async () => {
    try {
      const saveData: any = { ...formData };
      
      if (formData.location === ScooterBatteryLocation.WAREHOUSE) {
        saveData.scooter = null;
        saveData.chargingStation = null;
      } else if (formData.location === ScooterBatteryLocation.CHARGING_STATION) {
        saveData.scooter = null;
      } else if (formData.location === ScooterBatteryLocation.SCOOTER) {
        saveData.chargingStation = null;
      }

      if (editingBattery) {
        await updateBattery({
          id: editingBattery._id,
          body: saveData as CreateBatteryBody
        }).unwrap();
      } else {
        await createBattery(saveData as CreateBatteryBody).unwrap();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save battery:', error);
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm(t('batteries.confirmDeleteBattery'))) {
      try {
        await deleteBattery(id).unwrap();
      } catch (error) {
        console.error('Failed to delete battery:', error);
      }
    }
  };

  const handleOpenBulkDialog = () => {
    const actonType = scooterTypesData?.data.find(t => t.name.includes('Acton M Pro 2.0'));
    setBulkFormData({
      prefix: 'ACT-',
      startNumber: 1004,
      quantity: 800,
      scooterType: actonType?._id || ''
    });
    setBulkProgress({ current: 0, total: 0, isRunning: false, errors: [] });
    setBulkDialogOpen(true);
  };

  const handleCloseBulkDialog = () => {
    if (!bulkProgress.isRunning) {
      setBulkDialogOpen(false);
    }
  };

  const handleToggleBattery = (batteryId: string) => {
    const newSelected = new Set(selectedBatteries);
    if (newSelected.has(batteryId)) {
      newSelected.delete(batteryId);
    } else {
      newSelected.add(batteryId);
    }
    setSelectedBatteries(newSelected);
  };

  const handleToggleAllBatteries = () => {
    if (!filteredBatteries) return;
    if (selectedBatteries.size === filteredBatteries.length) {
      setSelectedBatteries(new Set());
    } else {
      setSelectedBatteries(new Set(filteredBatteries.map(b => b._id)));
    }
  };

  const handleBulkDelete = async () => {
    if (selectedBatteries.size === 0) {
      alert(t('batteries.alertSelectToDelete'));
      return;
    }

    if (!window.confirm(t('batteries.confirmBulkDelete', { count: selectedBatteries.size }))) {
      return;
    }

    const errors: string[] = [];
    for (const batteryId of Array.from(selectedBatteries)) {
      try {
        await deleteBattery(batteryId).unwrap();
      } catch (error: any) {
        errors.push(batteryId);
      }
    }

    if (errors.length === 0) {
      alert(t('batteries.alertBulkDeleteSuccess', { count: selectedBatteries.size }));
      setSelectedBatteries(new Set());
    } else {
      alert(t('batteries.alertBulkDeletePartial', { count: selectedBatteries.size - errors.length, errors: errors.length }));
    }
  };

  const handleBulkCreate = async () => {
    if (!bulkFormData.scooterType || bulkFormData.quantity <= 0) {
      alert(t('batteries.alertSelectTypeAndQuantity'));
      return;
    }

    const errors: string[] = [];
    setBulkProgress({ current: 0, total: bulkFormData.quantity, isRunning: true, errors: [] });

    for (let i = 0; i < bulkFormData.quantity; i++) {
      const code = `${bulkFormData.prefix}${bulkFormData.startNumber + i}`;
      try {
        await createBattery({
          code,
          comment: '',
          scooterTypes: [bulkFormData.scooterType],
          type: ScooterBatteryType.REPLACEABLE,
          status: ScooterBatteryStatus.ACTIVE,
          chargeLevel: 100,
          location: ScooterBatteryLocation.WAREHOUSE,
          scooter: null,
          chargingStation: null,
          faultCount: 0
        } as CreateBatteryBody).unwrap();
      } catch (error: any) {
        errors.push(`${code}: ${error?.data?.message || t('batteries.errorGeneric')}`);
      }
      setBulkProgress(prev => ({ ...prev, current: i + 1, errors: [...errors] }));
    }

    setBulkProgress(prev => ({ ...prev, isRunning: false }));
    
    if (errors.length === 0) {
      alert(t('batteries.alertBulkCreateSuccess', { count: bulkFormData.quantity }));
      setBulkDialogOpen(false);
    } else {
      alert(t('batteries.alertBulkCreatePartial', { count: bulkFormData.quantity - errors.length, errors: errors.length }));
    }
  };

  const getStatusColor = (status: ScooterBatteryStatus) => {
    switch (status) {
      case ScooterBatteryStatus.ACTIVE:
        return 'success';
      case ScooterBatteryStatus.CHARGING:
        return 'info';
      case ScooterBatteryStatus.MAINTENANCE:
        return 'warning';
      case ScooterBatteryStatus.REMOVED:
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: ScooterBatteryStatus) => {
    const labels = {
      [ScooterBatteryStatus.ACTIVE]: t('batteries.statusActive'),
      [ScooterBatteryStatus.CHARGING]: t('batteries.statusCharging'),
      [ScooterBatteryStatus.MAINTENANCE]: t('batteries.statusMaintenance'),
      [ScooterBatteryStatus.REMOVED]: t('batteries.statusRemoved')
    };
    return labels[status];
  };

  const getLocationLabel = (location: ScooterBatteryLocation) => {
    const labels = {
      [ScooterBatteryLocation.SCOOTER]: t('batteries.locationScooter'),
      [ScooterBatteryLocation.CHARGING_STATION]: t('batteries.locationChargingStation'),
      [ScooterBatteryLocation.WAREHOUSE]: t('batteries.locationWarehouse')
    };
    return labels[location];
  };

  const generateQRWithText = async (code: string): Promise<string> => {
    const qrSize = 500;
    const textHeight = 140;
    const totalHeight = qrSize + textHeight;
    
    const qrCanvas = document.createElement('canvas');
    await QRCode.toCanvas(qrCanvas, code, {
      width: qrSize,
      margin: 2
    });
    
    const finalCanvas = document.createElement('canvas');
    finalCanvas.width = qrSize;
    finalCanvas.height = totalHeight;
    const ctx = finalCanvas.getContext('2d');
    
    if (!ctx) throw new Error('Canvas context not available');
    
    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, qrSize, totalHeight);
    
    ctx.drawImage(qrCanvas, 0, 0);
    
    ctx.fillStyle = '#000000';
    ctx.font = 'bold 96px Arial';
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(code, qrSize / 2, qrSize + textHeight / 2);
    
    return finalCanvas.toDataURL('image/png');
  };

  const handleDownloadQR = async (code: string) => {
    try {
      const qrDataUrl = await generateQRWithText(code);
      
      const link = document.createElement('a');
      link.href = qrDataUrl;
      link.download = `${code}.png`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Failed to generate QR code:', error);
    }
  };

  const handleDownloadAllQR = async () => {
    try {
      const queryAction: any = await dispatch(
        batteriesApi.endpoints.getBatteries.initiate({ limit: 10000 })
      );
      
      if (queryAction.error || !queryAction.data?.data || queryAction.data.data.length === 0) {
        alert(t('batteries.alertNoBatteriesForQR'));
        queryAction.unsubscribe?.();
        return;
      }

      const zip = new JSZip();
      
      for (const battery of queryAction.data.data) {
        const qrDataUrl = await generateQRWithText(battery.code);
        
        const base64Data = qrDataUrl.split(',')[1];
        zip.file(`${battery.code}.png`, base64Data, { base64: true });
      }

      const blob = await zip.generateAsync({ type: 'blob' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'battery_qr_codes.zip';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(link.href);
      
      queryAction.unsubscribe?.();
    } catch (error) {
      console.error('Failed to generate QR codes archive:', error);
      alert(t('batteries.alertQRArchiveError'));
    }
  };

  return (
    <Box>
      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Typography variant="body2" color="textSecondary">{t('batteries.statActive')}</Typography>
              <Typography variant="h5" color="success.main">{stats.active}</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Typography variant="body2" color="textSecondary">{t('batteries.statCharging')}</Typography>
              <Typography variant="h5" color="info.main">{stats.charging}</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Typography variant="body2" color="textSecondary">{t('batteries.statMaintenance')}</Typography>
              <Typography variant="h5" color="warning.main">{stats.maintenance}</Typography>
            </StatCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StatCard>
              <Typography variant="body2" color="textSecondary">{t('batteries.statHealthLt60')}</Typography>
              <Typography variant="h5" color="error.main">{stats.healthLt60}</Typography>
            </StatCard>
          </Grid>
        </Grid>
      )}

      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2} gap={2} flexWrap="wrap">
        <Box display="flex" gap={2} flexWrap="wrap" flex={1}>
          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>{t('batteries.scooterTypeLabel')}</InputLabel>
            <Select
              value={scooterTypeFilter}
              onChange={e => setScooterTypeFilter(e.target.value)}
              label={t('batteries.scooterTypeLabel')}
            >
              <MenuItem value="">{t('batteries.allTypes')}</MenuItem>
              {scooterTypesData?.data.map(type => (
                <MenuItem key={type._id} value={type._id}>
                  {type.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label={t('batteries.searchCodeLabel')}
            value={searchCode}
            onChange={e => setSearchCode(e.target.value)}
            size="small"
            sx={{ minWidth: 200 }}
            placeholder={t('batteries.searchCodePlaceholder')}
          />
          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>{t('batteries.statusLabel')}</InputLabel>
            <Select
              value={statusFilter}
              onChange={e => setStatusFilter(e.target.value as ScooterBatteryStatus | '')}
              label={t('batteries.statusLabel')}
            >
              <MenuItem value="">{t('batteries.allStatuses')}</MenuItem>
              <MenuItem value={ScooterBatteryStatus.ACTIVE}>{t('batteries.statusActive')}</MenuItem>
              <MenuItem value={ScooterBatteryStatus.CHARGING}>{t('batteries.statusCharging')}</MenuItem>
              <MenuItem value={ScooterBatteryStatus.MAINTENANCE}>{t('batteries.statusMaintenance')}</MenuItem>
              <MenuItem value={ScooterBatteryStatus.REMOVED}>{t('batteries.statusRemoved')}</MenuItem>
            </Select>
          </FormControl>
        </Box>
        <Box display="flex" gap={2} flexWrap="wrap">
          {selectedBatteries.size > 0 && (
            <Button
              variant="contained"
              color="error"
              startIcon={<DeleteOutline />}
              onClick={handleBulkDelete}
            >
              {t('batteries.bulkDeleteBtn', { count: selectedBatteries.size })}
            </Button>
          )}
          <Button
            variant="outlined"
            startIcon={<Archive />}
            onClick={handleDownloadAllQR}
            disabled={!batteriesData?.data || batteriesData.data.length === 0}
          >
            {t('batteries.downloadAllQRBtn')}
          </Button>
          <Button
            variant="outlined"
            startIcon={<PlaylistAdd />}
            onClick={handleOpenBulkDialog}
            color="secondary"
          >
            {t('batteries.bulkAddBtn')}
          </Button>
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => handleOpenDialog()}
          >
            {t('batteries.addBatteryBtn')}
          </Button>
        </Box>
      </Box>

      <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : !batteriesData?.data || batteriesData.data.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              {t('batteries.noBatteries')}
            </Typography>
          </Box>
        ) : filteredBatteries.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              {t('batteries.noBatteriesMatchFilters')}
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell sx={{ width: 50, p: 0, textAlign: 'center' }}>
                  <IconButton
                    size="small"
                    onClick={handleToggleAllBatteries}
                    sx={{ p: 0 }}
                  >
                    {filteredBatteries && selectedBatteries.size === filteredBatteries.length ? (
                      <Checkbox checked size="small" />
                    ) : (
                      <Checkbox indeterminate={selectedBatteries.size > 0 && filteredBatteries && selectedBatteries.size < filteredBatteries.length} size="small" />
                    )}
                  </IconButton>
                </TableHeaderCell>
                <TableHeaderCell>{t('batteries.colCode')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colScooterTypes')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colBatteryType')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colLocation')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colStatus')}</TableHeaderCell>
                <TableHeaderCell>{t('batteries.colQRCode')}</TableHeaderCell>
                <TableHeaderCell align="right">{t('batteries.colActions')}</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredBatteries.map(battery => (
                <StyledTableRow key={battery._id}>
                  <TableCell sx={{ width: 50, p: 0, textAlign: 'center' }}>
                    <IconButton
                      size="small"
                      onClick={() => handleToggleBattery(battery._id)}
                      sx={{ p: 0 }}
                    >
                      <Checkbox checked={selectedBatteries.has(battery._id)} size="small" />
                    </IconButton>
                  </TableCell>
                  <StyledTableCell>{battery.code}</StyledTableCell>
                  <StyledTableCell>
                    {battery.scooterTypes && Array.isArray(battery.scooterTypes) && battery.scooterTypes.length > 0
                      ? battery.scooterTypes
                          .map(t => (typeof t === 'string' ? t : t.name))
                          .join(', ')
                      : '-'}
                  </StyledTableCell>
                  <StyledTableCell>
                    {battery.type === ScooterBatteryType.REPLACEABLE ? t('batteries.typeReplaceable') : t('batteries.typeNonReplaceable')}
                  </StyledTableCell>
                  <StyledTableCell>
                    {getLocationLabel(battery.location)}
                    {battery.scooter && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {battery.scooter.name}
                      </Typography>
                    )}
                    {battery.chargingStation && (
                      <Typography variant="caption" display="block" color="textSecondary">
                        {battery.chargingStation.name}
                      </Typography>
                    )}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={getStatusLabel(battery.status)}
                      color={getStatusColor(battery.status)}
                      size="small"
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<Download />}
                      onClick={() => handleDownloadQR(battery.code)}
                    >
                      {t('batteries.downloadQRBtn')}
                    </Button>
                  </StyledTableCell>
                  <StyledTableCell align="right">
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      <IconButton
                        size="small"
                        onClick={() => handleOpenDialog(battery)}
                        title={t('batteries.editTooltip')}
                      >
                        <Edit fontSize="small" />
                      </IconButton>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(battery._id)}
                        title={t('batteries.deleteTooltip')}
                      >
                        <Delete fontSize="small" />
                      </IconButton>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {batteriesData && batteriesData.pagination.totalPages > 1 && filteredBatteries.length > 0 && (
        <PaginationWrapper>
          <MuiPagination
            count={batteriesData.pagination.totalPages}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            color="primary"
          />
        </PaginationWrapper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {editingBattery ? t('batteries.editBatteryTitle') : t('batteries.addBatteryTitle')}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('batteries.colCode')}
                  value={formData.code}
                  onChange={e => setFormData({ ...formData, code: e.target.value })}
                  fullWidth
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  label={t('batteries.fieldCommentLabel')}
                  value={formData.comment}
                  onChange={e => setFormData({ ...formData, comment: e.target.value })}
                  fullWidth
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <FormControl fullWidth>
                  <InputLabel>{t('batteries.colScooterTypes')}</InputLabel>
                  <Select
                    multiple
                    value={formData.scooterTypes || []}
                    onChange={e => setFormData({ ...formData, scooterTypes: typeof e.target.value === 'string' ? [e.target.value] : e.target.value })}
                    label={t('batteries.colScooterTypes')}
                  >
                    {scooterTypesData?.data.map(type => (
                      <MenuItem key={type._id} value={type._id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('batteries.colType')}</InputLabel>
                  <Select
                    value={formData.type}
                    onChange={e => setFormData({ ...formData, type: e.target.value as ScooterBatteryType })}
                    label={t('batteries.colType')}
                  >
                    <MenuItem value={ScooterBatteryType.REPLACEABLE}>{t('batteries.typeReplaceable')}</MenuItem>
                    <MenuItem value={ScooterBatteryType.NON_REPLACEABLE}>{t('batteries.typeNonReplaceable')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('batteries.statusLabel')}</InputLabel>
                  <Select
                    value={formData.status}
                    onChange={e => setFormData({ ...formData, status: e.target.value as ScooterBatteryStatus })}
                    label={t('batteries.statusLabel')}
                  >
                    <MenuItem value={ScooterBatteryStatus.ACTIVE}>{t('batteries.statusActive')}</MenuItem>
                    <MenuItem value={ScooterBatteryStatus.CHARGING}>{t('batteries.statusCharging')}</MenuItem>
                    <MenuItem value={ScooterBatteryStatus.MAINTENANCE}>{t('batteries.statusMaintenance')}</MenuItem>
                    <MenuItem value={ScooterBatteryStatus.REMOVED}>{t('batteries.statusRemoved')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>{t('batteries.colLocation')}</InputLabel>
                  <Select
                    value={formData.location}
                    onChange={e => setFormData({ ...formData, location: e.target.value as ScooterBatteryLocation })}
                    label={t('batteries.colLocation')}
                  >
                    <MenuItem value={ScooterBatteryLocation.WAREHOUSE}>{t('batteries.locationWarehouse')}</MenuItem>
                    <MenuItem value={ScooterBatteryLocation.SCOOTER}>{t('batteries.locationScooter')}</MenuItem>
                    <MenuItem value={ScooterBatteryLocation.CHARGING_STATION}>{t('batteries.locationChargingStation')}</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              {formData.location === ScooterBatteryLocation.CHARGING_STATION && (
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth>
                    <InputLabel>{t('batteries.colChargingStation')}</InputLabel>
                    <Select
                      value={formData.chargingStation || ''}
                      onChange={e => setFormData({ ...formData, chargingStation: e.target.value })}
                      label={t('batteries.colChargingStation')}
                    >
                      {stationsData?.data.map(station => (
                        <MenuItem key={station._id} value={station._id}>
                          {station.name}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
              )}
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('batteries.fieldChargeLevel')}
                  type="number"
                  value={formData.chargeLevel}
                  onChange={e => setFormData({ ...formData, chargeLevel: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0, max: 100 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('batteries.fieldFaultCount')}
                  type="number"
                  value={formData.faultCount}
                  onChange={e => setFormData({ ...formData, faultCount: Number(e.target.value) })}
                  fullWidth
                  inputProps={{ min: 0 }}
                />
              </Grid>
            </Grid>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>{t('batteries.cancelBtn')}</Button>
          <Button onClick={handleSave} variant="contained">
            {t('batteries.saveBtn')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={bulkDialogOpen} onClose={handleCloseBulkDialog} maxWidth="sm" fullWidth>
        <DialogTitle>{t('batteries.bulkAddTitle')}</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('batteries.fieldCodePrefix')}
                  value={bulkFormData.prefix}
                  onChange={e => setBulkFormData({ ...bulkFormData, prefix: e.target.value })}
                  fullWidth
                  disabled={bulkProgress.isRunning}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('batteries.fieldStartNumber')}
                  type="number"
                  value={bulkFormData.startNumber}
                  onChange={e => setBulkFormData({ ...bulkFormData, startNumber: Number(e.target.value) })}
                  fullWidth
                  disabled={bulkProgress.isRunning}
                  inputProps={{ min: 1 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  label={t('batteries.fieldQuantity')}
                  type="number"
                  value={bulkFormData.quantity}
                  onChange={e => setBulkFormData({ ...bulkFormData, quantity: Number(e.target.value) })}
                  fullWidth
                  disabled={bulkProgress.isRunning}
                  inputProps={{ min: 1, max: 1000 }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth disabled={bulkProgress.isRunning}>
                  <InputLabel>{t('batteries.scooterTypeLabel')}</InputLabel>
                  <Select
                    value={bulkFormData.scooterType}
                    onChange={e => setBulkFormData({ ...bulkFormData, scooterType: e.target.value })}
                    label={t('batteries.scooterTypeLabel')}
                  >
                    {scooterTypesData?.data.map(type => (
                      <MenuItem key={type._id} value={type._id}>
                        {type.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Grid>
            </Grid>
            
            <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
              <Typography variant="body2" color="textSecondary">
                {t('batteries.bulkRangePrefix')}<strong>{bulkFormData.prefix}{bulkFormData.startNumber}</strong>{t('batteries.bulkRangeSeparator')}<strong>{bulkFormData.prefix}{bulkFormData.startNumber + bulkFormData.quantity - 1}</strong>
              </Typography>
            </Box>

            {bulkProgress.isRunning && (
              <Box sx={{ mt: 2 }}>
                <Box display="flex" alignItems="center" gap={2} mb={1}>
                  <CircularProgress size={24} />
                  <Typography>
                    {t('batteries.bulkCreatedProgress', { current: bulkProgress.current, total: bulkProgress.total })}
                  </Typography>
                </Box>
                <Box sx={{ width: '100%', bgcolor: 'grey.200', borderRadius: 1, height: 8 }}>
                  <Box
                    sx={{
                      width: `${(bulkProgress.current / bulkProgress.total) * 100}%`,
                      bgcolor: 'primary.main',
                      borderRadius: 1,
                      height: '100%',
                      transition: 'width 0.3s'
                    }}
                  />
                </Box>
                {bulkProgress.errors.length > 0 && (
                  <Typography variant="body2" color="error" sx={{ mt: 1 }}>
                    {t('batteries.bulkErrorsCount', { count: bulkProgress.errors.length })}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseBulkDialog} disabled={bulkProgress.isRunning}>
            {t('batteries.cancelBtn')}
          </Button>
          <Button
            onClick={handleBulkCreate}
            variant="contained"
            disabled={bulkProgress.isRunning || !bulkFormData.scooterType}
          >
            {bulkProgress.isRunning ? t('batteries.bulkCreating') : t('batteries.bulkCreate')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default BatteriesTab;
